import ArrowBackOutlined from "@mui/icons-material/ArrowBackOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import BuildOutlined from "@mui/icons-material/BuildOutlined";
import ExpandMoreOutlined from "@mui/icons-material/ExpandMoreOutlined";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import BadgeOutlined from "@mui/icons-material/BadgeOutlined";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import { Bot } from "lucide-react";
import ChatBubbleOutlined from "@mui/icons-material/ChatBubbleOutlined";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { formatTimestamp, hostname, parseError, sanitizeFilename } from "../../utils";
import LoadingButton from "../../components/LoadingButton";
import { ConversationData } from "./Conversations";

interface ConversationMessage {
  id: string;
  messageType: "user" | "agent" | "draft_agent" | "bot";
  messageContent: string;
  timestamp: number;
  replyToMessageId?: string;
  model?: string;
  tokensUsed?: number;
  responseTime?: number;
  ttft?: number;
  isSuccessful?: boolean;
  errorMessage?: string;
  userIpAddress?: string;
  userAgent?: string;
  status?: string;
  attachments?: Array<{
    name: string;
    content: string;
    type: string;
  }>;
  debugMetadata?: {
    tool_calls?: Array<{
      tool_name: string;
      arguments: any;
      tool_call_id: string;
      timestamp: number;
      server_id?: string;
      actual_tool_name?: string;
      result?: any;
      success?: boolean;
    }>;
    error_details?: {
      error_type: string;
      error_message: string;
      context: string;
      timestamp: number;
      traceback: string;
    };
  };
  channelParams?: {
    subject?: string;
    from?: string;
    to?: string;
    messageId?: string;
    phoneNumber?: string;
  };
}

interface ConversationDetails {
  agentId: string;
  agentName: string;
  agentAvatarImage?: string;
  sessionId: string;
  messageCount: number;
  messages: ConversationMessage[];
  firstMessageTime: number;
  lastMessageTime: number;
}

interface ConversationDetailPanelProps {
  conversation: ConversationData;
  getToken: Function;
  companyId: string;
  onClose?: () => void;
  getContactDisplayName: (conversation: ConversationData) => string;
}

const ConversationDetailPanel: React.FC<ConversationDetailPanelProps> = ({
  conversation,
  getToken,
  companyId,
  onClose,
  getContactDisplayName,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [details, setDetails] = useState<ConversationDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<string>("");
  const [approveLoading, setApproveLoading] = useState<string | null>(null);
  const [rejectLoading, setRejectLoading] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const fetchConversationDetails = async () => {
    if (!conversation) return;

    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/conversations/${conversation.agentId}/${conversation.sessionId}?companyId=${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setDetails(response.data);
    } catch (err) {
      console.error("Error fetching conversation details:", err);
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (conversation) {
      fetchConversationDetails();
    }
  }, [conversation]);

  // Scroll to bottom when messages load
  useEffect(() => {
    if (details && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [details]);

  const getMessageIcon = (messageType: "user" | "agent" | "draft_agent") => {
    return messageType === "user" ? (
      <PersonOutlined sx={{ color: "white", fontSize: 18 }} />
    ) : (
      <Bot size={18} style={{ color: "white" }} />
    );
  };

  const handleApproveDraft = async (draftId: string) => {
    setApproveLoading(draftId);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/agents/${conversation.agentId}/conversations/${conversation.sessionId}/drafts/${draftId}/send`,
        { companyId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await fetchConversationDetails();
    } catch (err) {
      console.error("Error sending draft:", err);
      setError(parseError(err));
    } finally {
      setApproveLoading(null);
    }
  };

  const handleRejectDraft = async (draftId: string) => {
    setRejectLoading(draftId);
    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/agents/${conversation.agentId}/conversations/${conversation.sessionId}/drafts/${draftId}?companyId=${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await fetchConversationDetails();
    } catch (err) {
      console.error("Error deleting draft:", err);
      setError(parseError(err));
    } finally {
      setRejectLoading(null);
    }
  };

  const handleEditDraft = (draftId: string, content: string) => {
    setEditingDraftId(draftId);
    setEditedContent(content);
  };

  const handleSaveEdit = async (draftId: string) => {
    setEditLoading(draftId);
    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/agents/${conversation.agentId}/conversations/${conversation.sessionId}/drafts/${draftId}`,
        { messageContent: editedContent, companyId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEditingDraftId(null);
      setEditedContent("");
      await fetchConversationDetails();
    } catch (err) {
      console.error("Error updating draft:", err);
      setError(parseError(err));
    } finally {
      setEditLoading(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingDraftId(null);
    setEditedContent("");
  };

  const renderErrorDebugInfo = (message: ConversationMessage) => {
    if (!message.debugMetadata?.error_details) {
      return null;
    }

    const errorDetails = message.debugMetadata.error_details;

    return (
      <Box sx={{ mt: 1 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
            <Box display="flex" alignItems="center" gap={1}>
              <CancelOutlined sx={{ fontSize: 16, color: "error.main" }} />
              <Typography variant="caption" color="error.main">
                Error Details
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: "error.light",
                  borderRadius: 1,
                  bgcolor: "error.light",
                  opacity: 0.8,
                }}
              >
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1}>
                  <Chip
                    label={errorDetails.error_type}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                  <Chip
                    label={errorDetails.context}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatTimestamp(errorDetails.timestamp * 1000).time}
                  </Typography>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" fontWeight="bold">Error Message:</Typography>
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.5,
                      p: 1,
                      bgcolor: "background.paper",
                      borderRadius: 0.5,
                      fontSize: "0.7rem",
                      maxHeight: 150,
                      overflow: "auto",
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    {errorDetails.error_message}
                  </Box>
                </Box>

                {errorDetails.traceback && (
                  <Box>
                    <Typography variant="caption" fontWeight="bold">Traceback:</Typography>
                    <Box
                      component="pre"
                      sx={{
                        mt: 0.5,
                        p: 1,
                        bgcolor: "background.paper",
                        borderRadius: 0.5,
                        fontSize: "0.7rem",
                        maxHeight: 200,
                        overflow: "auto",
                        border: 1,
                        borderColor: "divider",
                      }}
                    >
                      {errorDetails.traceback}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const renderToolCallDebugInfo = (message: ConversationMessage) => {
    if (!message.debugMetadata?.tool_calls || message.debugMetadata.tool_calls.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 1 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
            <Box display="flex" alignItems="center" gap={1}>
              <BuildOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                Tool Calls ({message.debugMetadata.tool_calls.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {message.debugMetadata.tool_calls.map((toolCall, index) => (
                <Box
                  key={toolCall.tool_call_id || index}
                  sx={{
                    p: 2,
                  }}
                >
                  <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1}>
                    <Chip
                      label={toolCall.actual_tool_name || toolCall.tool_name}
                      size="small"
                      variant="outlined"
                    />
                    {toolCall.server_id && (
                      <Chip
                        label={`Server: ${toolCall.server_id}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    <Chip
                      label={toolCall.success ? "Success" : "Failed"}
                      size="small"
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(toolCall.timestamp * 1000).time}
                    </Typography>
                  </Box>

                  {Object.keys(toolCall.arguments || {}).length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" fontWeight="bold">Arguments:</Typography>
                      <Box
                        component="pre"
                        sx={{
                          mt: 0.5,
                          p: 1,
                          bgcolor: "background.paper",
                          borderRadius: 0.5,
                          fontSize: "0.7rem",
                          maxHeight: 150,
                          overflow: "auto",
                          border: 1,
                          borderColor: "divider",
                        }}
                      >
                        {JSON.stringify(toolCall.arguments, null, 2)}
                      </Box>
                    </Box>
                  )}

                  {toolCall.result && (
                    <Box>
                      <Typography variant="caption" fontWeight="bold">Result:</Typography>
                      <Box
                        component="pre"
                        sx={{
                          mt: 0.5,
                          p: 1,
                          bgcolor: "background.paper",
                          borderRadius: 0.5,
                          fontSize: "0.7rem",
                          maxHeight: 200,
                          overflow: "auto",
                          border: 1,
                          borderColor: "divider",
                        }}
                      >
                        {typeof toolCall.result === 'string'
                          ? toolCall.result
                          : JSON.stringify(toolCall.result, null, 2)}
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  };

  const contactName = getContactDisplayName(conversation);
  const contactInfo = conversation.contactInfo || {};

  // Check if the contact name looks like a phone number
  const isPhoneNumber = (name: string) => {
    return /^[+\d\s\-()]+$/.test(name) && name.length >= 7;
  };

  // Get display-worthy contact fields (exclude internal fields starting with _)
  const getDisplayContactFields = () => {
    const fields: Array<{ key: string; label: string; value: string; icon: React.ReactNode }> = [];

    // Define field mappings with icons and labels
    const fieldConfig: Record<string, { label: string; icon: React.ReactNode }> = {
      email: { label: "Email", icon: <EmailOutlined fontSize="small" /> },
      phone: { label: "Phone", icon: <PhoneOutlined fontSize="small" /> },
      phoneNumber: { label: "Phone", icon: <PhoneOutlined fontSize="small" /> },
      "Phone Number": { label: "Phone", icon: <PhoneOutlined fontSize="small" /> },
      company: { label: "Company", icon: <BusinessOutlined fontSize="small" /> },
      jobTitle: { label: "Job Title", icon: <BadgeOutlined fontSize="small" /> },
      title: { label: "Title", icon: <BadgeOutlined fontSize="small" /> },
      website: { label: "Website", icon: <LinkOutlined fontSize="small" /> },
      linkedinUrl: { label: "LinkedIn", icon: <LinkOutlined fontSize="small" /> },
      "LinkedIn URL": { label: "LinkedIn", icon: <LinkOutlined fontSize="small" /> },
    };

    // Track which fields we've added to avoid duplicates
    const addedFields = new Set<string>();

    for (const [key, value] of Object.entries(contactInfo)) {
      // Skip internal fields, name fields (shown in header), and empty values
      if (
        key.startsWith("_") ||
        key === "firstName" ||
        key === "lastName" ||
        key === "name" ||
        key === "First Name" ||
        key === "Last Name" ||
        !value ||
        typeof value !== "string"
      ) {
        continue;
      }

      const config = fieldConfig[key];
      const normalizedKey = key.toLowerCase().replace(/\s+/g, "");

      // Skip if we already have this field type
      if (
        (normalizedKey.includes("phone") && addedFields.has("phone")) ||
        (normalizedKey.includes("email") && addedFields.has("email"))
      ) {
        continue;
      }

      if (config) {
        fields.push({
          key,
          label: config.label,
          value: value as string,
          icon: config.icon,
        });
        if (normalizedKey.includes("phone")) addedFields.add("phone");
        if (normalizedKey.includes("email")) addedFields.add("email");
      } else {
        // For custom fields, create a readable label
        const label = key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
        fields.push({
          key,
          label,
          value: value as string,
          icon: <BadgeOutlined fontSize="small" />,
        });
      }
    }

    return fields;
  };

  const displayFields = getDisplayContactFields();

  // Get avatar content - use icon for phone numbers, initials for names
  const getAvatarContent = () => {
    if (contactName === "Anonymous") return "?";
    if (isPhoneNumber(contactName)) {
      return <PhoneOutlined />;
    }
    // Get initials from name
    const parts = contactName.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return contactName.substring(0, 2).toUpperCase();
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
        }}
      >
        {onClose && (
          <IconButton onClick={onClose} size="small">
            <ArrowBackOutlined />
          </IconButton>
        )}
        <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
          {getAvatarContent()}
        </Avatar>
        <Box flex={1}>
          <Typography variant="subtitle1" fontWeight="medium">
            {contactName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {conversation.agentName}
          </Typography>
        </Box>
      </Box>

      {/* Contact Info Fields */}
      {displayFields.length > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "action.hover",
            display: "flex",
            flexWrap: "nowrap",
            gap: 1.5,
            overflow: "hidden",
          }}
        >
          {displayFields.map((field) => (
            <Box
              key={field.key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center" }}>
                {field.icon}
              </Box>
              <Typography variant="body2" color="text.secondary" noWrap>
                {field.label}:
              </Typography>
              <Typography variant="body2" fontWeight="medium" noWrap>
                {field.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Loading indicator */}
      {loading && <LinearProgress />}

      {/* Error message */}
      {error && (
        <Box sx={{ p: 2, bgcolor: "error.light" }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
        }}
      >
        {details?.messages.map((message) => {
          const isUser = message.messageType === "user";
          const isDraft = message.messageType === "draft_agent";

          return (
            <Box
              key={message.id}
              sx={{
                mb: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: isUser ? "flex-end" : "flex-start",
              }}
            >
              {/* Avatar and message row */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isUser ? "row-reverse" : "row",
                  alignItems: "flex-end",
                  gap: 1,
                  maxWidth: "80%",
                }}
              >
                {/* Avatar */}
                <Avatar
                  src={!isUser ? details.agentAvatarImage : undefined}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: isUser ? "grey.500" : isDraft ? "warning.main" : "secondary.main",
                    flexShrink: 0,
                  }}
                >
                  {isUser ? (
                    <PersonOutlined sx={{ fontSize: 18 }} />
                  ) : !details.agentAvatarImage ? (
                    <Bot size={18} />
                  ) : null}
                </Avatar>

                {/* Message content */}
                <Box>
                  {/* Email Subject (for draft_agent messages) */}
                  {isDraft && message.channelParams?.subject && (
                    <Box
                      sx={{
                        mb: 0.5,
                        px: 1,
                        py: 0.5,
                        bgcolor: "info.light",
                        borderRadius: 1,
                        fontSize: "0.75rem",
                      }}
                    >
                      Subject: {message.channelParams.subject}
                    </Box>
                  )}

                  {/* Message bubble */}
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: isUser
                        ? isDarkMode ? "grey.800" : "grey.300"
                        : isDraft
                        ? "warning.light"
                        : "background.paper",
                      color: isUser
                        ? isDarkMode ? "grey.100" : "grey.900"
                        : "text.primary",
                      border: isDraft ? 2 : 0,
                      borderColor: isDraft ? "warning.main" : "transparent",
                      boxShadow: 0,
                    }}
                  >
                    {isDraft && editingDraftId === message.id ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {message.messageContent}
                      </Typography>
                    )}
                  </Box>

                  {/* Timestamp and metrics */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.5,
                      px: 0.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography variant="caption" color="text.disabled">
                      {formatTimestamp(message.timestamp).time}
                    </Typography>
                    {/* Agent message metrics */}
                    {(message.messageType === "agent" || message.messageType === "bot") && (
                      <>
                        {message.ttft && (
                          <Chip
                            label={`TTFT: ${Math.round(message.ttft)}ms`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                        {message.responseTime && (
                          <Chip
                            label={`Total: ${Math.round(message.responseTime)}ms`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                        {message.model && (
                          <Chip
                            label={message.model}
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Attachments */}
              {message.attachments && message.attachments.length > 0 && (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    gap: 0.5,
                    flexWrap: "wrap",
                    justifyContent: isUser ? "flex-end" : "flex-start",
                    maxWidth: "80%",
                    pl: isUser ? 0 : 5,
                    pr: isUser ? 5 : 0,
                  }}
                >
                  {message.attachments.map((attachment, idx) => (
                    <Chip
                      key={idx}
                      icon={<ImageOutlined />}
                      label={attachment.name}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const blob = new Blob([attachment.content], { type: attachment.type });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = sanitizeFilename(attachment.name, "attachment");
                        link.click();
                        URL.revokeObjectURL(url);
                      }}
                      sx={{ cursor: "pointer" }}
                    />
                  ))}
                </Box>
              )}

              {/* Error debug info */}
              {!isUser && (
                <Box sx={{ maxWidth: "80%", pl: 5 }}>
                  {renderErrorDebugInfo(message)}
                  {renderToolCallDebugInfo(message)}
                </Box>
              )}

              {/* Draft actions */}
              {isDraft && message.status === "draft" && (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    gap: 1,
                    pl: 5,
                  }}
                >
                  {editingDraftId === message.id ? (
                    <>
                      <LoadingButton
                        size="small"
                        variant="contained"
                        color="primary"
                        loading={editLoading === message.id}
                        onClick={() => handleSaveEdit(message.id)}
                        startIcon={<CheckCircleOutline />}
                      >
                        Save
                      </LoadingButton>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleCancelEdit}
                        startIcon={<CancelOutlined />}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <LoadingButton
                        size="small"
                        variant="contained"
                        color="primary"
                        loading={approveLoading === message.id}
                        onClick={() => handleApproveDraft(message.id)}
                        startIcon={<CheckCircleOutline />}
                      >
                        Approve & Send
                      </LoadingButton>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => handleEditDraft(message.id, message.messageContent)}
                        startIcon={<EditOutlined />}
                      >
                        Edit
                      </Button>
                      <LoadingButton
                        size="small"
                        variant="outlined"
                        color="error"
                        loading={rejectLoading === message.id}
                        onClick={() => handleRejectDraft(message.id)}
                        startIcon={<CancelOutlined />}
                      >
                        Reject
                      </LoadingButton>
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        })}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />

        {/* Empty state */}
        {details?.messages.length === 0 && !loading && (
          <Box textAlign="center" py={4}>
            <ChatBubbleOutlined sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
            <Typography color="text.secondary">
              No messages in this conversation
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ConversationDetailPanel;
